import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";

const WhatsAppIcon = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="bg-forest-950 text-bone-100 relative overflow-hidden border-t border-bone-100/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-24 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-6">
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight mb-4">
              A broker who <br />
              <span className="italic text-terracotta-400">actually answers</span> the phone.
            </h2>
            <div className="overline text-terracotta-400 mb-8">Get in touch</div>
            <div className="space-y-4 text-bone-100/85 max-w-md">
              <a
                data-testid="footer-email-link"
                href="mailto:Alwaysinsured@hotmail.com"
                className="block text-lg link-underline w-fit"
              >
                Alwaysinsured@hotmail.com
              </a>
              <a
                data-testid="footer-phone-link"
                href="tel:+14163153679"
                className="block text-lg link-underline w-fit"
              >
                (416) 315&#8209;3679
              </a>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <a
                  data-testid="footer-whatsapp-link"
                  href="https://wa.me/14163153679"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp — alwaysinsured"
                  title="Message us on WhatsApp"
                  className="group inline-flex items-center gap-2.5 border border-bone-100/25 pl-3 pr-4 py-2 text-sm hover:bg-[#25D366] hover:border-[#25D366] hover:text-forest-900 transition-all"
                >
                  <span className="text-[#25D366] group-hover:text-forest-900 transition-colors">
                    <WhatsAppIcon size={18} />
                  </span>
                  <span>WhatsApp</span>
                </a>
                <a
                  data-testid="footer-instagram-link"
                  href="https://instagram.com/alwaysinsured"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram — @alwaysinsured"
                  title="Follow us on Instagram"
                  className="group inline-flex items-center gap-2.5 border border-bone-100/25 pl-3 pr-4 py-2 text-sm hover:border-transparent hover:text-bone-100 transition-all relative overflow-hidden"
                  style={{
                    backgroundImage: "linear-gradient(45deg, transparent 100%, transparent 100%)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "";
                  }}
                >
                  <Instagram size={18} className="text-[#DD2A7B] group-hover:text-bone-100 transition-colors" />
                  <span>@alwaysinsured</span>
                </a>
              </div>
              <p className="text-sm text-bone-100/60 pt-2">
                Licensed insurance brokerage · Auto · Home · Commercial
              </p>
            </div>
          </div>

          <div className="md:col-span-3">
            <ul className="space-y-3 text-bone-100/85">
              <li><Link to="/quote?type=auto" data-testid="footer-link-auto" className="link-underline">Auto Insurance</Link></li>
              <li><Link to="/quote?type=home" data-testid="footer-link-home" className="link-underline">Home Insurance</Link></li>
              <li><Link to="/quote?type=commercial" data-testid="footer-link-commercial" className="link-underline">Commercial Insurance</Link></li>
            </ul>
            <div className="overline text-bone-100/50 mt-5">Coverage</div>
          </div>

          <div className="md:col-span-3">
            <ul className="space-y-3 text-bone-100/85">
              <li><a href="#process" className="link-underline">Our Process</a></li>
              <li><a href="#faq" className="link-underline">FAQ</a></li>
              <li><Link to="/quote" data-testid="footer-cta-quote" className="link-underline">Get a Quote</Link></li>
              <li><Link to="/logo" data-testid="footer-brand-assets" className="link-underline">Brand assets</Link></li>
            </ul>
            <div className="overline text-bone-100/50 mt-5">Firm</div>
          </div>
        </div>

        <div className="mt-24 mb-4 select-none">
          <div
            className="font-serif leading-[0.9] tracking-tight text-bone-100 whitespace-nowrap"
            style={{ fontSize: "clamp(3rem, 13vw, 12rem)" }}
          >
            Always<span className="italic text-terracotta-400">Insured</span>
          </div>
          <div
            data-testid="footer-motto"
            className="font-serif italic text-xl md:text-2xl text-terracotta-400 tracking-tight mt-2 md:mt-0"
          >
            Always there, in all ways possible.
          </div>
        </div>

        <div className="border-t border-bone-100/15 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between text-xs text-bone-100/50 gap-3">
          <div>© {new Date().getFullYear()} AlwaysInsured. All rights reserved.</div>
          <div>alwaysinsured.ca · A licensed insurance brokerage.</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
